import React, { useState, useRef, useEffect } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  Eye,
  Minus,
  Plus,
  Grid3X3,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import { CardData } from '../types';

interface LeanOfficeStatusBarProps {
  /** Posição do mouse no canvas (coordenadas do VSM) */
  mousePosition?: { x: number; y: number };
  /** Status de salvamento do projeto */
  saveStatus?: 'saved' | 'saving' | 'unsaved';
  /** Cards selecionados no canvas (para cálculo da calculadora consultiva) */
  selectedCards?: CardData[];
  /** Todas os cards do projeto (para validação de dados) */
  allCards?: CardData[];
  /** Nível de zoom atual (0.25 a 2.0) */
  zoom?: number;
  /** Callback para alterar o zoom */
  onZoomChange?: (zoom: number) => void;
  /** Estado das camadas de visualização */
  layerSettings?: {
    showAS: boolean;
    showFTE: boolean;
    showGrid: boolean;
    showPercentBadge: boolean;
  };
  /** Callback para alternar camadas */
  onLayerToggle?: (layer: 'showAS' | 'showFTE' | 'showGrid' | 'showPercentBadge') => void;
  /** Callback para selecionar e focar em um card específico */
  onSelectCard?: (cardId: string) => void;
}

export const LeanOfficeStatusBar: React.FC<LeanOfficeStatusBarProps> = ({
  mousePosition = { x: 0, y: 0 },
  saveStatus = 'saved',
  selectedCards = [],
  allCards = [],
  zoom = 1.0,
  onZoomChange,
  layerSettings = { showAS: true, showFTE: true, showGrid: false, showPercentBadge: true },
  onLayerToggle,
  onSelectCard,
}) => {
  const [showLayerMenu, setShowLayerMenu] = useState(false);
  const [showHealthMenu, setShowHealthMenu] = useState(false);
  const layerMenuRef = useRef<HTMLDivElement>(null);
  const healthMenuRef = useRef<HTMLDivElement>(null);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (layerMenuRef.current && !layerMenuRef.current.contains(event.target as Node)) {
        setShowLayerMenu(false);
      }
      if (healthMenuRef.current && !healthMenuRef.current.contains(event.target as Node)) {
        setShowHealthMenu(false);
      }
    };

    if (showLayerMenu || showHealthMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLayerMenu, showHealthMenu]);

  // ========== SEÇÃO 2: CALCULADORA CONSULTIVA ==========
  const calculateSums = () => {
    if (selectedCards.length < 2) return null;

    let sumTRA = 0;
    let sumTP = 0;
    let sumFTE = 0;

    selectedCards.forEach((card) => {
      // TRA (Tempo de Realização da Atividade) - anteriormente 'tt'
      const tra = typeof card.tt === 'number' ? card.tt : 0;
      sumTRA += tra;

      // TP (Tempo de Permanência / Lead Time) - anteriormente 'tp'
      const tp = typeof card.tp === 'number' ? card.tp : (typeof card.tp === 'string' ? parseFloat(card.tp) || 0 : 0);
      sumTP += tp;

      // FTE (Full Time Equivalent) - calculado a partir de resources ou campo 'p'
      if (card.resources && card.resources.length > 0) {
        const fte = card.resources.reduce((acc, r) => {
          const dedication = (r.dedication || 100) / 100;
          return acc + r.quantity * dedication;
        }, 0);
        sumFTE += fte;
      } else if (card.p) {
        sumFTE += card.p;
      }
    });

    return {
      tra: sumTRA,
      tp: sumTP,
      fte: sumFTE,
    };
  };

  const sums = calculateSums();

  // ========== SEÇÃO 3: VALIDAÇÃO DE DADOS (POKA-YOKE) ==========
  const getIncompleteCards = () => {
    return allCards.filter((card) => {
      if (card.status === 'removido') return false;
      if (card.tipo === 'card_inicial' || card.tipo === 'card_final') return false;

      const hasTRA = card.tt !== undefined && card.tt !== null && card.tt !== 0;
      const hasAS = card.percentCompleteAccurate !== undefined && card.percentCompleteAccurate !== null;
      const hasFTE =
        (card.resources && card.resources.length > 0) ||
        (card.p !== undefined && card.p !== null && card.p !== 0);

      return !hasTRA || !hasAS || !hasFTE;
    });
  };

  // Detectar problemas de conexão e campos não preenchidos
  const detectHealthIssues = () => {
    const issues: Array<{ type: 'connection' | 'data'; cardId: string; cardName: string; details: string }> = [];

    allCards.forEach((card) => {
      if (card.status === 'removido') return;
      if (card.tipo === 'card_inicial' || card.tipo === 'card_final') return;

      const cardName = card.atividade || card.nome_fluxo || 'Sem nome';

      // Verificar campos incompletos
      const missingFields: string[] = [];
      if (!card.tt || card.tt === 0) missingFields.push('TRA');
      if (card.percentCompleteAccurate === undefined || card.percentCompleteAccurate === null) missingFields.push('AS%');
      if (!card.resources?.length && (!card.p || card.p === 0)) missingFields.push('FTE');

      if (missingFields.length > 0) {
        issues.push({
          type: 'data',
          cardId: card.id,
          cardName,
          details: `Campos vazios: ${missingFields.join(', ')}`,
        });
      }

      // Verificar conexões (cards sem predecessores ou sucessores, exceto início e fim)
      // Nota: Isso requer informação de edges/conexões que não está disponível aqui
      // Por enquanto, vamos focar apenas em dados incompletos
    });

    return issues;
  };

  const healthIssues = detectHealthIssues();
  const incompleteCards = getIncompleteCards();
  const hasIncompleteData = incompleteCards.length > 0;

  // ========== FUNÇÕES DE ZOOM ==========
  const handleZoomIn = () => {
    if (onZoomChange) {
      onZoomChange(Math.min(2.0, zoom + 0.1));
    }
  };

  const handleZoomOut = () => {
    if (onZoomChange) {
      onZoomChange(Math.max(0.25, zoom - 0.1));
    }
  };

  const handleZoomReset = () => {
    if (onZoomChange) {
      onZoomChange(1.0);
    }
  };

  // ========== RENDERIZAÇÃO ==========
  return (
    <div className="enterprise-statusbar-surface fixed bottom-0 left-0 right-0 z-50 flex h-10 items-center justify-between border-t border-enterprise-border-strong px-4 text-xs select-none">
      {/* ========== SEÇÃO 1: TELEMETRIA (ESQUERDA) ========== */}
      <div className="flex items-center gap-4">
        {/* Coordenadas do Mouse */}
        <div className="flex items-center gap-1 font-mono text-[10px] text-enterprise-text-secondary">
          <span className="font-semibold">X:</span>
          <span className="w-8 text-right">{Math.round(mousePosition.x)}</span>
          <span className="font-semibold ml-1">Y:</span>
          <span className="w-8 text-right">{Math.round(mousePosition.y)}</span>
        </div>

        {/* Divisor Vertical */}
        <div className="h-5 w-px bg-enterprise-border-strong" />

        {/* Status de Salvamento */}
        <div className="flex items-center gap-1.5">
          {saveStatus === 'saved' && (
            <>
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="risk-ok font-medium">Salvo</span>
            </>
          )}
          {saveStatus === 'saving' && (
            <>
              <RefreshCw className="risk-alert h-3 w-3 animate-spin" />
              <span className="risk-alert font-medium">Sincronizando...</span>
            </>
          )}
          {saveStatus === 'unsaved' && (
            <>
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="risk-alert font-medium">Não salvo</span>
            </>
          )}
        </div>
      </div>

      {/* ========== SEÇÃO 2: CALCULADORA CONSULTIVA (CENTRO) ========== */}
      {sums && (
        <div className="enterprise-surface-1 flex items-center gap-3 rounded-md border border-enterprise-border-strong px-4 py-1">
          <span className="text-[10px] font-semibold text-enterprise-text-secondary">
            {selectedCards.length} selecionados
          </span>
          <div className="h-4 w-px bg-enterprise-border-strong" />
          <div className="flex items-center gap-3 font-mono text-[10px]">
            <div className="flex items-center gap-1">
              <span className="font-semibold text-enterprise-text-muted">SUM TRA:</span>
              <span className="enterprise-tech-value font-bold">{sums.tra.toFixed(1)}min</span>
            </div>
            <div className="h-3 w-px bg-enterprise-border-strong" />
            <div className="flex items-center gap-1">
              <span className="font-semibold text-enterprise-text-muted">SUM TP:</span>
              <span className="enterprise-tech-value font-bold">{sums.tp.toFixed(1)}min</span>
            </div>
            <div className="h-3 w-px bg-enterprise-border-strong" />
            <div className="flex items-center gap-1">
              <span className="font-semibold text-enterprise-text-muted">SUM FTE:</span>
              <span className="enterprise-tech-value font-bold">{sums.fte.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ========== SEÇÃO 3 & 4: VALIDAÇÃO + CONTROLES (DIREITA) ========== */}
      <div className="flex items-center gap-3">
        {/* Botão de Validação (Poka-Yoke) com Menu Dropdown */}
        <div className="relative" ref={healthMenuRef}>
          <button
            type="button"
            onClick={() => setShowHealthMenu(!showHealthMenu)}
            title={
              hasIncompleteData
                ? `${healthIssues.length} campo${healthIssues.length > 1 ? 's' : ''} pendente${healthIssues.length > 1 ? 's' : ''}`
                : 'Dados completos'
            }
            className={`flex items-center gap-1.5 px-2 py-1 rounded transition-all ${
              hasIncompleteData
                ? 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-emerald-600 dark:text-emerald-500'
            }`}
          >
            {hasIncompleteData ? (
              <>
                <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="font-medium text-[10px] text-slate-500 dark:text-slate-400">
                  {healthIssues.length}
                </span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="font-medium text-[10px]">OK</span>
              </>
            )}
          </button>

          {/* Menu Dropdown de Problemas */}
          {showHealthMenu && healthIssues.length > 0 && (
            <div className="absolute bottom-full right-0 mb-2 w-72 max-h-80 overflow-y-auto bg-white/98 dark:bg-[var(--bg-surface)]/98 backdrop-blur-md rounded shadow-lg border border-slate-200 dark:border-slate-700 py-1.5 z-50">
              <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                  Campos pendentes ({healthIssues.length})
                </span>
              </div>

              <div className="py-0.5">
                {healthIssues.map((issue, index) => (
                  <button
                    key={`${issue.cardId}-${index}`}
                    onClick={() => {
                      if (onSelectCard) {
                        onSelectCard(issue.cardId);
                        setShowHealthMenu(false);
                      }
                    }}
                    className="w-full px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer text-left group"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-medium text-slate-700 dark:text-slate-200 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {issue.cardName}
                        </div>
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                          {issue.details}
                        </div>
                      </div>
                      <span className="text-[9px] text-slate-300 dark:text-slate-600 group-hover:text-blue-400 transition-colors">→</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Divisor Vertical */}
        <div className="h-5 w-px bg-enterprise-border-strong" />

        {/* Toggle de Camadas */}
        <div className="relative" ref={layerMenuRef}>
          <button
            type="button"
            onClick={() => setShowLayerMenu(!showLayerMenu)}
            className="flex items-center gap-1.5 rounded px-2.5 py-1 text-enterprise-text-primary transition-colors hover:bg-enterprise-panel"
            title="Visualizar Camadas"
          >
            <Eye className="w-4 h-4" strokeWidth={2} />
            <span className="font-medium text-[10px]">Visualizar</span>
          </button>

          {/* Menu Popover de Camadas */}
          {showLayerMenu && (
            <div className="absolute bottom-full right-0 mb-2 w-48 bg-white/95 dark:bg-[var(--bg-surface)]/95 backdrop-blur-md rounded shadow-xl border border-slate-200 dark:border-slate-700 py-2 z-50">
              <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Camadas
              </div>
              <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />
              
              {/* Indicadores AS% */}
              <button
                type="button"
                onClick={() => onLayerToggle?.('showAS')}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                    layerSettings.showAS
                      ? 'bg-blue-500 border-blue-500'
                      : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'
                  }`}
                >
                  {layerSettings.showAS && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <span className="text-xs text-slate-700 dark:text-slate-300">Indicadores AS%</span>
              </button>

              {/* Dados de FTE */}
              <button
                type="button"
                onClick={() => onLayerToggle?.('showFTE')}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                    layerSettings.showFTE
                      ? 'bg-blue-500 border-blue-500'
                      : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'
                  }`}
                >
                  {layerSettings.showFTE && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <span className="text-xs text-slate-700 dark:text-slate-300">Dados de FTE</span>
              </button>

              {/* Grid */}
              <button
                type="button"
                onClick={() => onLayerToggle?.('showGrid')}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                    layerSettings.showGrid
                      ? 'bg-blue-500 border-blue-500'
                      : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'
                  }`}
                >
                  {layerSettings.showGrid && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <span className="text-xs text-slate-700 dark:text-slate-300">Grid</span>
              </button>

              <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />
              
              {/* Percentual nos Cards */}
              <button
                type="button"
                onClick={() => onLayerToggle?.('showPercentBadge')}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                    layerSettings.showPercentBadge
                      ? 'bg-blue-500 border-blue-500'
                      : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'
                  }`}
                >
                  {layerSettings.showPercentBadge && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <span className="text-xs text-slate-700 dark:text-slate-300">% Carga nos Cards</span>
              </button>
            </div>
          )}
        </div>

        {/* Divisor Vertical */}
        <div className="h-5 w-px bg-enterprise-border-strong" />

        {/* Controles de Zoom */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={zoom <= 0.25}
            className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-enterprise-panel disabled:cursor-not-allowed disabled:opacity-40"
            title="Reduzir Zoom"
          >
            <Minus className="h-4 w-4 text-enterprise-text-secondary" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={handleZoomReset}
            className="flex h-7 min-w-[52px] items-center justify-center rounded px-2.5 font-mono text-[11px] font-medium text-enterprise-text-primary transition-colors hover:bg-enterprise-panel"
            title="Resetar Zoom (100%)"
          >
            {Math.round(zoom * 100)}%
          </button>

          <button
            type="button"
            onClick={handleZoomIn}
            disabled={zoom >= 2.0}
            className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-enterprise-panel disabled:cursor-not-allowed disabled:opacity-40"
            title="Aumentar Zoom"
          >
            <Plus className="h-4 w-4 text-enterprise-text-secondary" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};
